import {
  ArrowLeft,
  BarChart3,
  Bell,
  BookOpenCheck,
  ChevronDown,
  CircleUserRound,
  ClipboardList,
  Gavel,
  Heart,
  Home,
  LayoutDashboard,
  ListPlus,
  LogIn,
  MailPlus,
  Menu,
  PackageSearch,
  PackageCheck,
  ShoppingCart,
  Users,
  UsersRound,
  WifiOff,
  X,
  type LucideIcon,
} from "lucide-react";
import { confirmLogout } from "@/lib/offlineOrders";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useCart } from "@/contexts/CartContext";
import { useConnectivity } from "@/contexts/ConnectivityContext";
import { useTheme } from "@/contexts/ThemeContext";
import { IconButton } from "@/components/ui";
import { api } from "@/lib/api";
import { getUserTagPresentation } from "@/lib/userTag";
import { deliverDueListingExpiryReminders } from "@/lib/listingReminders";
import {
  getMobileHeaderAction,
  shouldShowMobileHeaderBrand,
  shouldUseMobileNavigationMenu,
} from "@/lib/mobileHeader";
import {
  showBrowserNotification,
  synchroniseWebPushPreference,
  wasWebPushDisabled,
} from "@/lib/webPush";
import type { UserRole } from "@/types/domain";

interface NavItem {
  label: string;
  to: string;
  icon: LucideIcon;
  roles?: UserRole[];
  authenticated?: boolean;
}

interface MobileNavItem {
  label: string;
  to: string;
  roles?: UserRole[];
}

const mobilePageTitles: Array<[RegExp, string]> = [
  [/^\/admin\/analytics\/?$/, "App Analytics"],
  [/^\/$/, "Home"],
  [/^\/(ai-chat|ai-search|search)\/?$/, "AI Chat"],
  [/^\/listings\/new\/?$/, "New Listing"],
  [/^\/listings\/preview\/?$|^\/previewListing\/?$/, "Listing Preview"],
  [/^\/listings\/[^/]+\/edit\/?$|^\/edit-listing\/[^/]+\/?$/, "Edit Listing"],
  [/^\/listing\/[^/]+\/?$/, "Listing Details"],
  [/^\/listings\/?$/, "Livestock Listings"],
  [/^\/watchlist\/?$/, "My Watchlist"],
  [/^\/my-listings\/?$/, "My Listings"],
  [/^\/drafts\/?$/, "My Drafts"],
  [/^\/notifications\/?$/, "Notifications"],
  [/^\/edit-profile\/?$/, "Edit Profile"],
  [/^\/profile\/?$/, "Profile"],
  [/^\/my-groups\/[^/]+\/?$/, "Group Details"],
  [/^\/my-groups\/?$/, "My Groups"],
  [/^\/(agent\/)?groups\/[^/]+\/?$/, "Group Details"],
  [/^\/(agent\/)?groups\/?$/, "Farmer Groups"],
  [/^\/auction\/[^/]+\/bid-history\/?$/, "Bid History"],
  [/^\/auction\/[^/]+\/?$/, "Auction Details"],
  [/^\/auctions\/?$/, "Auctions"],
  [/^\/admin\/users\/?$|^\/admin\/manageUsers\/?$/, "Manage Users"],
  [/^\/admin\/(assign-agent|assignAgent)\/?$/, "Assign Agent"],
  [/^\/admin\/create-agent\/?$/, "Create Agent"],
  [/^\/admin\/(invitations|send-invitation)\/?$/, "Invitations"],
  [/^\/admin\/(bulk-upload|bulk-upload-farmers)\/?$/, "Bulk Upload Farmers"],
  [/^\/admin\/(reports|farmerAgentReport)\/?$/, "Farmer-Agent Report"],
  [/^\/admin\/(sold-listings|soldListings)\/?$/, "Sold Listings"],
  [/^\/admin\/(listings\/[^/]+\/review|review-listing\/[^/]+)\/?$/, "Review Listing"],
  [/^\/admin\/(auctions|auction-management)\/notifications\/?$/, "Auction Notifications"],
  [/^\/admin\/(auctions|auction-management)\/create(?:-auction)?\/?$/, "Create Auction"],
  [/^\/admin\/auctions\/[^/]+\/edit\/?$|^\/admin\/auction-management\/edit-auction\/?$/, "Edit Auction"],
  [/^\/admin\/(auctions|auction-management)(?:\/view\/[^/]+)?\/?$/, "Auction Management"],
  [/^\/admin(?:\/adminDashboard)?\/?$/, "Admin Dashboard"],
  [/^\/ordering\/customers\/?$|^\/ordering\/select-customer\/?$/, "Select Customer"],
  [/^\/ordering\/customer(?:-detail|\/[^/]+)\/?$/, "Customer Details"],
  [/^\/ordering\/(products|add-products)\/?$/, "Add Products"],
  [/^\/ordering\/(mixing|mixing-instructions)\/?$/, "Mixing Instructions"],
  [/^\/ordering\/(dispatch|dispatch-location)\/?$/, "Dispatch Location"],
  [/^\/ordering\/delivery\/?$/, "Delivery Details"],
  [/^\/ordering\/review\/?$/, "Review Order"],
  [/^\/ordering\/summary\/?$/, "Order Summary"],
  [/^\/ordering\/queue\/?$/, "Queued Orders"],
  [/^\/ordering\/(orders|order-details)\/[^/]+\/?$/, "Order Details"],
  [/^\/ordering\/?$/, "Agronomy Ordering"],
];

function getMobilePageTitle(pathname: string) {
  return mobilePageTitles.find(([pattern]) => pattern.test(pathname))?.[1] || "Carrfields";
}

const primaryNav: NavItem[] = [
  { label: "Home", to: "/", icon: Home },
  { label: "Livestock", to: "/listings", icon: PackageSearch },
  // { label: "Auctions", to: "/auctions", icon: Gavel },
  { label: "Watchlist", to: "/watchlist", icon: Heart, authenticated: true },
  {
    label: "Notifications",
    to: "/notifications",
    icon: Bell,
    roles: ["user", "farmer", "member", "agent", "admin"],
  },
  // {
  //   label: "My groups",
  //   to: "/my-groups",
  //   icon: UsersRound,
  //   roles: ["user", "farmer", "member"],
  // },
  {
    label: "Ordering",
    to: "/ordering",
    icon: ShoppingCart,
    roles: ["admin", "agronomist_admin", "agronomist_agent"],
  },
];

const managementNav: NavItem[] = [
  { label: "Analytics", to: "/admin/analytics", icon: BarChart3, roles: ["admin"] },
  { label: "Dashboard", to: "/admin", icon: LayoutDashboard, roles: ["admin"] },
  {
    label: "New listing",
    to: "/listings/new",
    icon: ListPlus,
    roles: ["admin", "agent"],
  },
  {
    label: "My listings",
    to: "/my-listings",
    icon: ClipboardList,
    roles: ["admin", "agent"],
  },
  {
    label: "Drafts",
    to: "/drafts",
    icon: BookOpenCheck,
    roles: ["admin", "agent"],
  },
  {
    label: "Farmer groups",
    to: "/groups",
    icon: UsersRound,
    roles: ["admin", "agent"],
  },
  {
    label: "Send invitations",
    to: "/admin/invitations",
    icon: MailPlus,
    roles: ["admin", "agent"],
  },
  {
    label: "Users & agents",
    to: "/admin/users",
    icon: Users,
    roles: ["admin"],
  },
  {
    label: "Auction management",
    to: "/admin/auctions",
    icon: Gavel,
    roles: ["admin"],
  },
  {
    label: "Sold listings",
    to: "/admin/sold-listings",
    icon: PackageCheck,
    roles: ["admin"],
  },
];

const mobileDrawerNav: MobileNavItem[] = [
  { label: "APP ANALYTICS", to: "/admin/analytics", roles: ["admin"] },
  { label: "MY WATCHLIST", to: "/watchlist" },
  { label: "MY DRAFTS", to: "/drafts", roles: ["agent", "admin"] },
  {
    label: "NEW LIVESTOCK LISTING",
    to: "/listings/new",
    roles: ["agent", "admin"],
  },
  {
    label: "MY LISTINGS",
    to: "/my-listings",
    roles: ["agent", "admin"],
  },
  { label: "DASHBOARD", to: "/admin", roles: ["admin"] },
  // { label: "AUCTION", to: "/auctions" },
  {
    label: "CREATE AUCTION",
    to: "/admin/auctions/create",
    roles: ["admin"],
  },
  {
    label: "NOTIFICATIONS",
    to: "/notifications",
    roles: ["agent", "admin", "user", "farmer", "member"],
  },
  {
    label: "SEND INVITATIONS",
    to: "/admin/invitations",
    roles: ["agent", "admin"],
  },
  {
    label: "NEW ORDER",
    to: "/ordering",
    roles: ["admin", "agronomist_agent"],
  },
  {
    label: "FARMER GROUPS",
    to: "/groups",
    roles: ["agent", "admin"],
  },
  // {
  //   label: "MY GROUPS",
  //   to: "/my-groups",
  //   roles: ["user", "farmer", "member"],
  // },
];

function visible(
  item: NavItem,
  role: UserRole | undefined,
  authenticated: boolean,
): boolean {
  if (item.authenticated && !authenticated) return false;
  return !item.roles || Boolean(role && item.roles.includes(role));
}

function NavItemLink({
  item,
  onClick,
  badge,
}: {
  item: NavItem;
  onClick?: () => void;
  badge?: number;
}) {
  const Icon = item.icon;
  return (
    <NavLink
      to={item.to}
      end={item.to === "/" || item.to === "/admin" || item.to === "/ordering"}
      onClick={onClick}
      className={({ isActive }) =>
        `nav-item${isActive ? " nav-item--active" : ""}`
      }
    >
      <Icon size={20} aria-hidden="true" />
      <span>{item.label}</span>
      {Boolean(badge) && badge! > 0 && (
        <span className="nav-item__badge">{badge}</span>
      )}
    </NavLink>
  );
}

function Brand({ compact = false }: { compact?: boolean }) {
  const { resolvedTheme } = useTheme();
  const darkLogo = resolvedTheme === "dark";
  const logoSource = darkLogo
    ? "/mobile-assets/images/carrfield-logo-dark.png"
    : "/brand/logo-light.png";
  const logoDimensions = darkLogo
    ? { width: 1012, height: 364 }
    : { width: 616, height: 220 };

  return (
    <NavLink
      to="/"
      className={`brand${compact ? " brand--compact" : ""}`}
      aria-label="Carrfields home"
    >
      <img
        src={logoSource}
        alt="Carrfields"
        width={logoDimensions.width}
        height={logoDimensions.height}
        decoding={compact ? "sync" : "async"}
        fetchPriority={compact ? "high" : "auto"}
      />
    </NavLink>
  );
}

function UserMenu() {
  const { user, isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  if (!isAuthenticated || !user) {
    return (
      <>
        <NavLink
          to="/login"
          className="topbar-mobile-account"
          aria-label="Sign in"
        >
          <CircleUserRound size={30} />
        </NavLink>
        <NavLink
          to="/login"
          className="button button--primary button--sm topbar-desktop-account"
        >
          <LogIn size={17} /> Sign in
        </NavLink>
      </>
    );
  }

  const initials =
    `${user.firstName?.[0] || user.name?.[0] || ""}${user.lastName?.[0] || ""}`.toUpperCase();
  return (
    <>
      <NavLink
        to="/profile"
        className="topbar-mobile-account"
        aria-label="Open profile"
      >
        <CircleUserRound size={30} />
      </NavLink>
      <div className="user-menu topbar-desktop-account">
        <button
          type="button"
          className="user-menu__trigger"
          onClick={() => setOpen((value) => !value)}
          aria-expanded={open}
        >
          {user.userImage ? (
            <img src={user.userImage} alt="" className="avatar" />
          ) : (
            <span className="avatar avatar--initials">
              {initials || <CircleUserRound size={20} />}
            </span>
          )}
          <span className="user-menu__identity">
            <strong>{user.firstName || user.name || "Account"}</strong>
            <small>{getUserTagPresentation(user.userType)?.label || "Account"}</small>
          </span>
          <ChevronDown size={16} />
        </button>
        {open && (
          <div className="user-menu__popover">
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                navigate("/profile");
              }}
            >
              Profile & preferences
            </button>
            <button
              type="button"
              onClick={async () => {
                setOpen(false);
                if (!(await confirmLogout())) return;
                await logout();
                navigate("/login");
              }}
            >
              Sign out
            </button>
          </div>
        )}
      </div>
    </>
  );
}
export function AppShell() {
  const { user, isAuthenticated, logout } = useAuth();
  const { isOnline, queueCount } = useConnectivity();
  const { cartCount } = useCart();
  const location = useLocation();
  const navigate = useNavigate();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const notificationPollingReady = useRef(false);
  const webPushSyncedFor = useRef("");
  const notificationPreview = useQuery({
    queryKey: ["notification-preview", user?.userId],
    queryFn: () => api.notifications.list(user!.userId),
    enabled: Boolean(user?.userId),
    refetchInterval: 60_000,
  });

  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    notificationPollingReady.current = false;
    webPushSyncedFor.current = "";
  }, [user?.userId]);

  useEffect(() => {
    if (!user?.userId) return;
    const deliver = () => {
      void deliverDueListingExpiryReminders(user.userId);
    };
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") deliver();
    };

    deliver();
    const interval = window.setInterval(deliver, 60_000);
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => {
      window.clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [user?.userId]);

  useEffect(() => {
    if (
      !user?.userId ||
      !("Notification" in window) ||
      Notification.permission !== "granted" ||
      webPushSyncedFor.current === user.userId
    )
      return;

    webPushSyncedFor.current = user.userId;
    void synchroniseWebPushPreference(user.userId).catch((error) => {
      webPushSyncedFor.current = "";
      console.warn("Unable to refresh the Web Push subscription.", error);
    });
  }, [user?.userId]);

  useEffect(() => {
    if (!user?.userId || !notificationPreview.data) return;
    const key = "carrfields-seen-notifications:" + user.userId;
    const current = notificationPreview.data.notifications;
    const existing = new Set<string>(
      JSON.parse(sessionStorage.getItem(key) || "[]") as string[],
    );
    if (
      notificationPollingReady.current &&
      "Notification" in window &&
      Notification.permission === "granted" &&
      !wasWebPushDisabled(user.userId)
    ) {
      current
        .filter(
          (item) =>
            !existing.has(item.notificationId) &&
            !(item.read ?? item.isRead ?? false),
        )
        .forEach((item) => {
          void showBrowserNotification(item.title || "Carrfields update", {
            body:
              item.message ||
              item.body ||
              "New activity is available in Carrfields.",
            icon: "/icons/icon-192.png",
            badge: "/icons/icon-192.png",
            tag: item.notificationId,
            actionUrl: item.actionUrl || "/notifications",
          });
        });
    }
    sessionStorage.setItem(
      key,
      JSON.stringify(
        [
          ...new Set([
            ...existing,
            ...current.map((item) => item.notificationId),
          ]),
        ].slice(-500),
      ),
    );
    notificationPollingReady.current = true;
  }, [notificationPreview.data, user?.userId]);

  const role = user?.userType;
  const unreadNotifications =
    notificationPreview.data?.unreadCount ||
    notificationPreview.data?.notifications.filter(
      (item) => !(item.read ?? item.isRead ?? false),
    ).length ||
    0;
  const mainItems = useMemo(
    () => primaryNav.filter((item) => visible(item, role, isAuthenticated)),
    [isAuthenticated, role],
  );
  const manageItems = useMemo(
    () => managementNav.filter((item) => visible(item, role, isAuthenticated)),
    [isAuthenticated, role],
  );
  const mobileItems = useMemo(
    () =>
      mobileDrawerNav.filter(
        (item) => !item.roles || Boolean(role && item.roles.includes(role)),
      ),
    [role],
  );
  const useMobileNavigationMenu = shouldUseMobileNavigationMenu(
    location.pathname,
  );
  const showMobileBrand = shouldShowMobileHeaderBrand(location.pathname);
  const mobileHeaderAction = getMobileHeaderAction(
    location.pathname,
    isAuthenticated,
  );
  const mobilePageTitle = getMobilePageTitle(location.pathname);

  const handleMobileBack = () => {
    if (useMobileNavigationMenu) {
      setDrawerOpen(true);
      return;
    }
    if (window.history.length > 1) {
      navigate(-1);
      return;
    }
    navigate("/");
  };

  return (
    <div className="app-shell">
      {!isOnline && (
        <div className="offline-banner" role="status">
          <WifiOff size={16} /> You are offline. Browsing uses saved data and
          new orders will be queued.
        </div>
      )}

      <aside className="desktop-sidebar">
        <Brand />
        <nav className="desktop-nav" aria-label="Primary navigation">
          <span className="nav-label">Explore</span>
          {mainItems.map((item) => (
            <NavItemLink
              item={item}
              key={item.to}
              badge={item.to === "/notifications" ? unreadNotifications : undefined}
            />
          ))}
          {manageItems.length > 0 && (
            <span className="nav-label nav-label--spaced">Manage</span>
          )}
          {manageItems.map((item) => (
            <NavItemLink item={item} key={item.to} />
          ))}
        </nav>
        <div className="desktop-sidebar__footer">
          <span>
            Livestock expertise,
            <br />
            wherever you are.
          </span>
          <small>© {new Date().getFullYear()} Carrfields</small>
        </div>
      </aside>

      <div className="app-shell__main">
        <header className="topbar">
          <div className="topbar__mobile-brand">
            <IconButton
              label={useMobileNavigationMenu ? "Open navigation" : "Go back"}
              onClick={handleMobileBack}
            >
              {useMobileNavigationMenu ? (
                <Menu size={24} />
              ) : (
                <ArrowLeft size={24} />
              )}
            </IconButton>
            {showMobileBrand ? (
              <Brand compact />
            ) : (
              <strong className="topbar__mobile-title">{mobilePageTitle}</strong>
            )}
            <NavLink
              to={mobileHeaderAction.to}
              className={
                mobileHeaderAction.kind === "account"
                  ? "topbar-mobile-account"
                  : "topbar__mobile-home"
              }
              aria-label={mobileHeaderAction.label}
            >
              {mobileHeaderAction.kind === "account" ? (
                <CircleUserRound size={30} />
              ) : (
                <Home size={24} />
              )}
            </NavLink>
          </div>
          <div className="topbar__context">
            <span className="eyebrow">Carrfields portal</span>
            <strong>
              {role?.startsWith("agronomist")
                ? "Agronomy ordering"
                : "Livestock marketplace"}
            </strong>
          </div>
          <div className="topbar__actions">
            {queueCount > 0 && (
              <NavLink
                to="/ordering/queue"
                className="status-pill"
                title="Queued orders"
              >
                <WifiOff size={16} /> {queueCount} queued
              </NavLink>
            )}
            {cartCount > 0 && (
              <NavLink
                to="/ordering/review"
                className="topbar-icon"
                aria-label={`${cartCount} products in order`}
              >
                <ShoppingCart size={20} />
                <span>{cartCount}</span>
              </NavLink>
            )}
            {isAuthenticated && !location.pathname.startsWith("/ordering") && (
              <NavLink
                to="/notifications"
                className="topbar-icon"
                aria-label={unreadNotifications + " unread notifications"}
              >
                <Bell size={20} />
                {unreadNotifications > 0 && <span>{unreadNotifications}</span>}
              </NavLink>
            )}
            <UserMenu />
          </div>
        </header>

        <main className="page-container" id="main-content">
          <Outlet />
        </main>
      </div>

      {drawerOpen && (
        <div
          className="mobile-drawer-backdrop"
          role="presentation"
          onMouseDown={(event) =>
            event.target === event.currentTarget && setDrawerOpen(false)
          }
        >
          <aside className="mobile-drawer" aria-label="Navigation menu">
            <div className="mobile-drawer__header">
              <IconButton
                label="Close navigation"
                onClick={() => setDrawerOpen(false)}
              >
                <X size={32} />
              </IconButton>
            </div>
            <div className="mobile-drawer__section-title">LIVESTOCK</div>
            <nav aria-label="Livestock navigation">
              {mobileItems.map((item) => (
                <NavLink
                  to={item.to}
                  key={item.label}
                  className="mobile-drawer__item"
                  onClick={() => setDrawerOpen(false)}
                >
                  {item.label}
                  {item.to === "/notifications" && unreadNotifications > 0 && (
                    <span className="nav-item__badge">
                      {unreadNotifications}
                    </span>
                  )}
                </NavLink>
              ))}
            </nav>
            <div className="mobile-drawer__bottom">
              <button
                type="button"
                onClick={async () => {
                  setDrawerOpen(false);
                  if (isAuthenticated) {
                    if (!(await confirmLogout())) return;
                    await logout();
                  }
                  navigate("/login");
                }}
              >
                {isAuthenticated ? "LOGOUT" : "LOGIN"}
              </button>
            </div>
          </aside>
        </div>
      )}
    </div>
  );
}

export function PlainLayout({ children }: { children: ReactNode }) {
  return (
    <div className="plain-layout">
      <header>
        <Brand />
      </header>
      <main>{children}</main>
    </div>
  );
}
